import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from '@lib/utils';
import type { ReactNode } from 'react';

type CyberScoreProps = {
  cpiBand?: number | null;
  cpiBandLabel?: string | null;
  cpiPercentage: number;
  label?: ReactNode;
  scoreClassName?: string;
  scoreContainerClassName?: string;
  scoreValueContainerClassName?: string;
  size?: 'lg' | 'md';
  subtitle?: ReactNode;
  tooltipContent?: ReactNode;
};

const CYBER_SCORE_SIZE_CLASS_NAME = {
  lg: {
    container: 'h-16 min-w-16 px-2',
    percentage: 'text-[40px]',
    suffix: 'mt-1 text-lg',
  },
  md: {
    container: 'h-12 min-w-12 px-2',
    percentage: 'text-2xl',
    suffix: 'mt-0.5 text-sm',
  },
} as const;

export const CyberScore = (props: CyberScoreProps) => {
  const {
    cpiBand,
    cpiBandLabel,
    cpiPercentage,
    label,
    scoreClassName,
    scoreContainerClassName,
    scoreValueContainerClassName,
    size = 'lg',
    subtitle,
    tooltipContent,
  } = props;

  const sizeClassName = CYBER_SCORE_SIZE_CLASS_NAME[size];

  const scoreContent = (
    <div className="flex items-start gap-3">
      <div
        className={cn(
          'flex items-center justify-center rounded-md bg-teal-500/10',
          sizeClassName.container,
          scoreContainerClassName,
        )}
      >
        <div className={cn('flex justify-center', scoreValueContainerClassName)}>
          <span className={cn('font-bold leading-none text-gray-800', sizeClassName.percentage, scoreClassName)}>
            {cpiPercentage}
          </span>
        </div>
        <span className={cn('font-bold text-gray-800', sizeClassName.suffix)}>%</span>
      </div>

      <div className={cn('flex flex-col items-start', size === 'lg' ? 'pt-2' : 'pt-1')}>
        <span className="text-xs font-semibold text-teal-600">{label ?? 'CPI'}</span>
        {subtitle ?? (
          <span className="text-xs font-light text-[#1F1C1AB2]">
            Band {cpiBand ?? '?'}: {cpiBandLabel ?? 'Unavailable'}
          </span>
        )}
      </div>
    </div>
  );

  if (!tooltipContent) {
    return scoreContent;
  }

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">{scoreContent}</div>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
